import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnmount } from 'ahooks';
import { Icon } from '@fuxi/eevee-icon';
import { Button, Dialog } from 'antd-mobile';
import { Toast, When } from '@/components';
import { SocketClientType } from '@/service/socketio';
import type { RefAttrs as Body2DViewerRef } from '@/components/body-2d-viewer';
import successImg from '@/assets/image/success-circle.svg';
import anchorImg from '@/assets/image/anchor.svg';
import bluePointImg from '@/assets/image/acupoint-blue.png';
import redPointImg from '@/assets/image/acupoint-red.png';
import { storagePrefixFixedV2 } from '@/constant';
import { PalpationEventBus } from '../../index';

import cx from './index.module.scss';

interface Props {
  isImageLoaded: boolean;
  body2DViewerRef: React.MutableRefObject<Body2DViewerRef>;
  socketInstance: React.MutableRefObject<SocketClientType>;
  isSocketConnected: boolean;
}

const ACUPOINT_SIZE = [40, 40];

const AcupointCalibration: React.FC<Props> = ({
  isImageLoaded,
  socketInstance,
  body2DViewerRef,
  isSocketConnected,
}) => {
  const [pointLists, setPointLists] = useState<GetSpinePointsResponse['points']>([]);
  const [isBtnLoading, setIsBtnLoading] = useState(false);

  const confirm = useCallback(
    ({ isSkip = false }) =>
      async () => {
        try {
          setIsBtnLoading(true);
          if (isSkip) {
            await socketInstance.current.skipSpineCalibration();
          } else {
            if (pointLists?.every(item => !item.deviation)) {
              await socketInstance.current.finishSpineCalibration();
            } else {
              Dialog.confirm({
                title: '确认使用当前结果标注吗？',
                content: '确认后将根据标注结果进行理疗服务',
                confirmText: '确认',
                async onConfirm() {
                  await socketInstance.current.finishSpineCalibration();
                },
              });
            }
          }
        } finally {
          setIsBtnLoading(false);
        }
      },
    [pointLists, socketInstance]
  );

  // 当前是否标记了胸椎
  const currentHasMarkedShoulder = useMemo(() => pointLists?.some(item => item.part === 'Shoulder'), [pointLists]);
  // 当前是否标记了腰椎
  const currentHasMarkedWaist = useMemo(() => pointLists?.some(item => item.part === 'Waist'), [pointLists]);
  // 胸椎是否有偏差
  const shoulderHasDeviation = useMemo(
    () => !!pointLists?.find(item => item.part === 'Shoulder')?.deviation,
    [pointLists]
  );
  // 腰椎是否有偏差
  const waistHasDeviation = useMemo(() => !!pointLists?.find(item => item.part === 'Waist')?.deviation, [pointLists]);

  const init = useCallback(async () => {
    PalpationEventBus.on('acupoint_calibration_repeated_marking', () => {
      Toast.warn('超出标记数量', { duration: 5000 });
    });
    PalpationEventBus.on('acupoint_calibration_spine_point_detected', event => {
      setPointLists(prev => [...(prev || []), ...event.details?.points]);
    });
    const data = await socketInstance.current.getSpinePoints();
    setPointLists(data?.points);
  }, [socketInstance]);

  const handleDelete = useCallback(
    (part: DeleteSpinePointsParams['parts'][0]) => async () => {
      if (!part) return;

      if (pointLists?.find(item => item.part === part)?.deviation) {
        await socketInstance.current.deleteSpinePoints({ parts: [part] });
        setPointLists(prev => prev.filter(item => item.part !== part));
      } else {
        Dialog.confirm({
          title: '确认删除该定位点吗？',
          content: '该操作将无法撤销',
          confirmText: '确认',
          onConfirm: async () => {
            await socketInstance.current.deleteSpinePoints({ parts: [part] });
            setPointLists(prev => prev.filter(item => item.part !== part));
          },
        });
      }
    },
    [pointLists, socketInstance]
  );

  useUnmount(() => {
    // 清除画的穴位矫正点
    body2DViewerRef.current?.drawImage?.([]);
  });

  // 图片上绘制穴位矫正点
  useEffect(() => {
    if (!isImageLoaded) return;

    body2DViewerRef.current?.drawImage(
      pointLists?.map(item => ({
        // 修正图片渲染偏差
        coord: item.coordinates?.map(item => item - 15),
        imageConfig: {
          url: item.deviation ? redPointImg : bluePointImg,
          size: ACUPOINT_SIZE,
        },
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageLoaded, pointLists]);

  useEffect(() => {
    if (isSocketConnected) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSocketConnected]);

  return (
    <div className={cx('AcupointCalibration')}>
      <div className={cx('AcupointCalibration-header')}>
        <div className={cx('main-title')}>触诊定位</div>
        <div className={cx('tips-content')}>
          <Icon name="info-circle" className={cx('tip-icon')} />
          <span>使用手诊手势标注即可</span>
        </div>
      </div>
      <div className={cx('AcupointCalibration-content')}>
        {!!pointLists?.length ? (
          <div className={cx('steps-content')}>
            {new Array(2).fill('').map((item, index) => (
              <div key={item.part} className={cx('step-item')}>
                <div className={cx('header')}>
                  <img
                    src={
                      index === 0
                        ? currentHasMarkedShoulder
                          ? successImg
                          : anchorImg
                        : currentHasMarkedWaist
                        ? successImg
                        : anchorImg
                    }
                    alt=""
                    className={cx('icon')}
                  />
                  <span className={cx('tip-text')}>
                    请标注
                    <span className={cx('highlight')}>{index == 0 ? '第三胸椎棘突下' : '第四腰椎'}</span>
                    位置
                  </span>
                </div>
                <div
                  className={cx('detect-info', {
                    warn: (index === 0 && shoulderHasDeviation) || (index === 1 && waistHasDeviation),
                    dotted: index === 0 ? !currentHasMarkedShoulder : !currentHasMarkedWaist,
                  })}
                >
                  <div className={cx('main-info')}>
                    <When condition={index === 0 ? currentHasMarkedShoulder : currentHasMarkedWaist}>
                      <Icon
                        name="close-drawer"
                        className={cx('icon')}
                        onClick={handleDelete(index === 0 ? 'Shoulder' : 'Waist')}
                      />
                    </When>
                    <div className={cx('name')}>{index === 0 ? '胸椎' : '腰椎'}</div>
                  </div>
                  {((index === 0 && shoulderHasDeviation) || (index === 1 && waistHasDeviation)) && (
                    <div className={cx('deviation-info')}>检测到您标注的点位与预测位置偏差较大</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={cx('tips-video')}>
            <video
              src={storagePrefixFixedV2 + '/videos/h264/kqwidqw.mp4'}
              poster={storagePrefixFixedV2 + '/image/asdqwfdwqfadqwdq-min.png'}
              muted
              autoPlay
              loop
            />
          </div>
        )}
      </div>

      <div className={cx('AcupointCalibration-footer')}>
        <Button loading={isBtnLoading} className={cx('cancel-btn')} onClick={confirm({ isSkip: true })}>
          跳过
        </Button>
        <Button
          color="primary"
          loading={isBtnLoading}
          disabled={pointLists?.length < 2}
          className={cx('confirm-btn')}
          onClick={confirm({ isSkip: false })}
        >
          确认触诊结果
        </Button>
      </div>
    </div>
  );
};

export default AcupointCalibration;
